import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { REFRESH_EVENT } from '../lib/notificationActions';
import { getDocLabel } from '../lib/docLabels';
import VideoIntroPlayer from '../components/VideoIntroPlayer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
    Users, DollarSign, BarChart3, Shield, Briefcase, Heart, User,
    TrendingUp, Activity, Ban, CheckCircle, Eye, FileText, X, Settings, Video, Plus, Search, Clock, ThumbsDown
} from 'lucide-react';
import DocumentPreviewModal from '../components/DocumentPreviewModal';

export default function AdminDashboard() {
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'users');
    const [users, setUsers] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [partners, setPartners] = useState([]);
    const [payouts, setPayouts] = useState([]);
    const [loading, setLoading] = useState(true);

    // Document viewer
    const [showDocs, setShowDocs] = useState(false);
    const [selectedUserDocs, setSelectedUserDocs] = useState([]);
    const [selectedUserName, setSelectedUserName] = useState('');
    const [selectedUserObj, setSelectedUserObj] = useState(null);
    const [roleFilter, setRoleFilter] = useState('all');
    const [approvalFilter, setApprovalFilter] = useState(searchParams.get('filter') || 'all'); // 'all' | 'pending' | 'approved' | 'rejected'
    const [userSearch, setUserSearch] = useState('');
    const [txnSearch, setTxnSearch] = useState('');
    const [txnSort, setTxnSort] = useState('date-desc'); // 'date-desc' | 'date-asc' | 'amount-desc' | 'amount-asc'
    const [previewDoc, setPreviewDoc] = useState(null);

    // Partner fee editor
    const [showFeeEditor, setShowFeeEditor] = useState(false);
    const [editingPartner, setEditingPartner] = useState(null);
    const [feeAmount, setFeeAmount] = useState('');
    const [commissionPct, setCommissionPct] = useState('');
    const [savingFees, setSavingFees] = useState(false);

    // Delete user
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deletingUserId, setDeletingUserId] = useState(null);
    const [deletingUserName, setDeletingUserName] = useState('');
    const [deleting, setDeleting] = useState(false);

    // Interests manager
    const [interestCategories, setInterestCategories] = useState([]);
    const [newCatName, setNewCatName] = useState('');
    const [newRoleName, setNewRoleName] = useState('');
    const [addingRoleTo, setAddingRoleTo] = useState(null);

    // Approval workflow
    const [showRejectDialog, setShowRejectDialog] = useState(false);
    const [rejectingUser, setRejectingUser] = useState(null);
    const [rejectReason, setRejectReason] = useState('');
    const [savingApproval, setSavingApproval] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            const [usersRes, txRes, analyticsRes, partnersRes, payoutsRes, interestsRes] = await Promise.all([
                api.get('/admin/users'),
                api.get('/admin/transactions'),
                api.get('/admin/analytics'),
                api.get('/admin/medical-partners'),
                api.get('/admin/payouts'),
                api.get('/admin/interests'),
            ]);
            setUsers(usersRes.data.users);
            setTransactions(txRes.data.transactions);
            setAnalytics(analyticsRes.data);
            setPartners(partnersRes.data.partners);
            setPayouts(payoutsRes.data.payouts);
            setInterestCategories(interestsRes.data.categories);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    // React to ?tab= / ?filter= changes (from notification click navigation)
    useEffect(() => {
        const t = searchParams.get('tab');
        if (t && ['users', 'partners', 'interests', 'transactions', 'submissions', 'settings', 'analytics'].includes(t)) {
            setActiveTab(t);
        }
        const f = searchParams.get('filter');
        if (f && ['all', 'pending', 'approved', 'rejected'].includes(f)) {
            setApprovalFilter(f);
        }
    }, [searchParams]);

    // Silent refresh when a notification arrives or is clicked
    useEffect(() => {
        const handler = (e) => {
            const keys = e.detail?.keys || [];
            if (keys.includes('users') || keys.includes('transactions')) {
                fetchData();
            }
        };
        window.addEventListener(REFRESH_EVENT, handler);
        return () => window.removeEventListener(REFRESH_EVENT, handler);
    }, [fetchData]);

    const toggleUserStatus = async (userId, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
        try { await api.put(`/admin/users/${userId}/status`, { status: newStatus }); await fetchData(); }
        catch (err) { console.error(err); }
    };

    const viewUserDocs = async (userId, userName) => {
        try {
            const { data } = await api.get(`/admin/documents/${userId}`);
            setSelectedUserDocs(data.documents);
            setSelectedUserName(userName);
            setSelectedUserObj(users.find(u => u.id === userId) || null);
            setShowDocs(true);
        } catch (err) { console.error(err); }
    };

    const openDocument = (doc) => {
        setPreviewDoc(doc);
    };

    const openFeeEditor = (partner) => {
        setEditingPartner(partner);
        setFeeAmount(String(partner.appointment_fee || ''));
        setCommissionPct(String(partner.commission_percent || ''));
        setShowFeeEditor(true);
    };

    const saveFees = async () => {
        if (!editingPartner) return;
        setSavingFees(true);
        try {
            await api.put(`/admin/medical-partners/${editingPartner.id}/fees`, {
                appointment_fee: parseFloat(feeAmount) || 120,
                commission_percent: parseFloat(commissionPct) || 40
            });
            setShowFeeEditor(false);
            await fetchData();
        } catch (err) { console.error(err); }
        finally { setSavingFees(false); }
    };

    const deleteJob = async (jobId) => {
        try { await api.delete(`/admin/jobs/${jobId}`); await fetchData(); }
        catch (err) { console.error(err); }
    };

    const confirmDeleteUser = (userId, userName) => {
        setDeletingUserId(userId);
        setDeletingUserName(userName);
        setShowDeleteConfirm(true);
    };

    const executeDeleteUser = async () => {
        setDeleting(true);
        try {
            await api.delete(`/admin/users/${deletingUserId}`);
            setShowDeleteConfirm(false);
            setDeletingUserId(null);
            await fetchData();
        } catch (err) { console.error(err); }
        finally { setDeleting(false); }
    };

    const addCategory = async () => {
        if (!newCatName.trim()) return;
        try {
            await api.post('/admin/interests/category', { name: newCatName.trim() });
            setNewCatName('');
            await fetchData();
        } catch (err) { console.error(err); }
    };

    const addRole = async (catId) => {
        if (!newRoleName.trim()) return;
        try {
            await api.post('/admin/interests/role', { category_id: catId, name: newRoleName.trim() });
            setNewRoleName('');
            setAddingRoleTo(null);
            await fetchData();
        } catch (err) { console.error(err); }
    };

    const toggleCategoryActive = async (catId, currentActive) => {
        try { await api.put(`/admin/interests/category/${catId}`, { active: !currentActive }); await fetchData(); }
        catch (err) { console.error(err); }
    };

    const toggleRoleActive = async (roleId, currentActive) => {
        try { await api.put(`/admin/interests/role/${roleId}`, { active: !currentActive }); await fetchData(); }
        catch (err) { console.error(err); }
    };

    const roleIcon = { worker: <User className="w-3.5 h-3.5" />, employer: <Briefcase className="w-3.5 h-3.5" />, medical: <Heart className="w-3.5 h-3.5" />, admin: <Shield className="w-3.5 h-3.5" /> };

    const approveUser = async (userId) => {
        setSavingApproval(true);
        try {
            await api.put(`/admin/users/${userId}/approval`, { action: 'approve' });
            await fetchData();
        } catch (err) { console.error(err); }
        finally { setSavingApproval(false); }
    };

    const openRejectDialog = (u) => {
        setRejectingUser(u);
        setRejectReason('');
        setShowRejectDialog(true);
    };

    const submitReject = async () => {
        if (!rejectingUser || !rejectReason.trim()) return;
        setSavingApproval(true);
        try {
            await api.put(`/admin/users/${rejectingUser.id}/approval`, { action: 'reject', reason: rejectReason.trim() });
            setShowRejectDialog(false);
            setRejectingUser(null);
            setRejectReason('');
            await fetchData();
        } catch (err) { console.error(err); }
        finally { setSavingApproval(false); }
    };

    const revokeApproval = async (userId) => {
        if (!window.confirm('Revoke approval and set this user back to pending?')) return;
        setSavingApproval(true);
        try {
            await api.put(`/admin/users/${userId}/approval`, { action: 'revoke' });
            await fetchData();
        } catch (err) { console.error(err); }
        finally { setSavingApproval(false); }
    };

    if (loading) return <div className="flex items-center justify-center h-[60vh]"><div className="w-8 h-8 border-2 border-zinc-900 border-t-transparent animate-spin" /></div>;

    // Pending approvals count that dynamically respects the current role filter.
    // Approval workflow only applies to employer and medical partners — workers
    // and admins are auto-active, so they never contribute to this total.
    // When roleFilter === 'all' this equals the global pending total; when a
    // specific role is selected, it narrows to that role only.
    const pendingCountForFilter = users.reduce((acc, u) => {
        if (u.role !== 'employer' && u.role !== 'medical') return acc;
        if (roleFilter !== 'all' && u.role !== roleFilter) return acc;
        if ((u.approval_status || 'approved') !== 'pending') return acc;
        return acc + 1;
    }, 0);

    return (
        <div data-testid="admin-dashboard">
            {/* Quick Stats Bar (Fix 7) */}
            {analytics && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mb-6" data-testid="quick-stats-bar">
                    <StatCard icon={<Users className="w-5 h-5" />} label="Workers" value={analytics.total_workers} sub={`${analytics.verified_workers || 0} verified`} testid="stat-workers" />
                    <StatCard icon={<Briefcase className="w-5 h-5" />} label="Employers" value={analytics.total_employers} testid="stat-employers" />
                    <StatCard icon={<Heart className="w-5 h-5" />} label="Medical Partners" value={analytics.total_medical_partners} testid="stat-medical" />
                    <StatCard icon={<Activity className="w-5 h-5" />} label="Offers Sent" value={analytics.total_offers} sub={`${analytics.accepted_offers || 0} accepted`} testid="stat-offers" />
                    <StatCard icon={<CheckCircle className="w-5 h-5" />} label="Visas Approved" value={analytics.visas_approved || 0} testid="stat-visas" />
                    <StatCard icon={<DollarSign className="w-5 h-5" />} label="Revenue" value={`$${(analytics.document_access_revenue || 0).toFixed(2)}`} testid="stat-revenue" />
                    <StatCard icon={<Clock className="w-5 h-5" />} label="Pending Approval" value={pendingCountForFilter} sub={roleFilter !== 'all' ? `${roleFilter}s only` : undefined} testid="stat-pending-approvals" />
                </div>
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid grid-cols-4 sm:grid-cols-7 w-full max-w-3xl rounded-none h-12 bg-zinc-100 mb-6">
                    <TabsTrigger value="users" className="rounded-none h-10 text-xs font-semibold data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="admin-users-tab">Users</TabsTrigger>
                    <TabsTrigger value="partners" className="rounded-none h-10 text-xs font-semibold data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="admin-partners-tab">Medical</TabsTrigger>
                    <TabsTrigger value="interests" className="rounded-none h-10 text-xs font-semibold data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="admin-interests-tab">Interests</TabsTrigger>
                    <TabsTrigger value="transactions" className="rounded-none h-10 text-xs font-semibold data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="admin-transactions-tab">Payments</TabsTrigger>
                    <TabsTrigger value="submissions" className="rounded-none h-10 text-xs font-semibold data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="admin-submissions-tab">Submissions</TabsTrigger>
                    <TabsTrigger value="settings" className="rounded-none h-10 text-xs font-semibold data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="admin-settings-tab">Settings</TabsTrigger>
                    <TabsTrigger value="analytics" className="rounded-none h-10 text-xs font-semibold data-[state=active]:bg-zinc-900 data-[state=active]:text-white" data-testid="admin-analytics-tab">Analytics</TabsTrigger>
                </TabsList>

                {/* Users Tab */}
                <TabsContent value="users">
                    <div className="flex flex-col sm:flex-row gap-3 mb-4" data-testid="users-controls">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                            <Input placeholder="Search by name, company, email..." value={userSearch} onChange={e => setUserSearch(e.target.value)} className="pl-10 h-11 rounded-none" data-testid="user-search-input" />
                        </div>
                    </div>
                    <div className="flex gap-2 mb-4 flex-wrap" data-testid="role-filter">
                        {['all', 'worker', 'employer', 'medical', 'admin'].map(r => (
                            <Button key={r} size="sm" variant={roleFilter === r ? 'default' : 'outline'} className="rounded-none text-xs h-8 capitalize" onClick={() => setRoleFilter(r)} data-testid={`filter-${r}`}>
                                {r === 'all' ? 'All' : r}
                            </Button>
                        ))}
                    </div>
                    <div className="flex gap-2 mb-4 flex-wrap items-center" data-testid="approval-filter">
                        <span className="text-xs text-zinc-500 uppercase tracking-wider mr-1">Approval:</span>
                        {['all', 'pending', 'approved', 'rejected'].map(s => (
                            <Button
                                key={s}
                                size="sm"
                                variant={approvalFilter === s ? 'default' : 'outline'}
                                className="rounded-none text-xs h-8 capitalize"
                                onClick={() => setApprovalFilter(s)}
                                data-testid={`approval-filter-${s}`}
                            >
                                {s}
                                {s === 'pending' && pendingCountForFilter > 0 && (
                                    <span className="ml-1.5 inline-flex items-center justify-center px-1.5 py-0.5 bg-amber-500 text-white text-[10px] font-bold rounded">{pendingCountForFilter}</span>
                                )}
                            </Button>
                        ))}
                    </div>
                    {(() => {
                        const q = userSearch.trim().toLowerCase();
                        const filtered = users.filter(u => {
                            if (roleFilter !== 'all' && u.role !== roleFilter) return false;
                            if (approvalFilter !== 'all') {
                                // Approval status only applies to employer/medical; show "approved" for others as default
                                const status = u.role === 'employer' || u.role === 'medical' ? (u.approval_status || 'approved') : null;
                                if (approvalFilter === 'pending' && status !== 'pending') return false;
                                if (approvalFilter === 'approved' && status !== 'approved') return false;
                                if (approvalFilter === 'rejected' && status !== 'rejected') return false;
                            }
                            if (!q) return true;
                            const fields = [u.name, u.company_name, u.email].filter(Boolean).map(s => String(s).toLowerCase());
                            return fields.some(f => f.includes(q));
                        });
                        if (filtered.length === 0) return (
                            <div className="text-center py-12 border border-zinc-200" data-testid="users-empty">
                                <Users className="w-10 h-10 text-zinc-200 mx-auto mb-3" />
                                <p className="text-sm text-zinc-500">{q || roleFilter !== 'all' || approvalFilter !== 'all' ? 'No users found matching your search.' : 'No users found matching your search or filter criteria.'}</p>
                            </div>
                        );
                        return (
                            <div className="border border-zinc-200 overflow-x-auto" data-testid="users-table">
                                <Table>
                                    <TableHeader><TableRow>
                                        <TableHead className="font-bold">Name</TableHead>
                                        <TableHead className="font-bold">Email</TableHead>
                                        <TableHead className="font-bold">Role</TableHead>
                                        <TableHead className="font-bold">Phone</TableHead>
                                        <TableHead className="font-bold">Status</TableHead>
                                        <TableHead className="font-bold">Approval</TableHead>
                                        <TableHead className="font-bold">Actions</TableHead>
                                    </TableRow></TableHeader>
                                    <TableBody>
                                        {filtered.map(u => {
                                            const needsApproval = u.role === 'employer' || u.role === 'medical';
                                            const approval = needsApproval ? (u.approval_status || 'approved') : null;
                                            return (
                                            <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                                                <TableCell className="font-medium text-sm">{u.name || u.company_name}</TableCell>
                                                <TableCell className="text-sm text-zinc-500">{u.email}</TableCell>
                                                <TableCell><div className="flex items-center gap-1.5">{roleIcon[u.role]}<span className="text-sm capitalize">{u.role}</span></div></TableCell>
                                                <TableCell data-testid={`phone-cell-${u.id}`}>
                                                    {u.phone ? (
                                                        <div className="flex items-center gap-1.5 text-sm">
                                                            <span className="font-mono">{u.phone}</span>
                                                            {!u.phone_verified && (
                                                                <Badge variant="outline" className="rounded-none text-[10px] px-1 py-0 border-zinc-300 text-zinc-500" data-testid={`phone-unverified-${u.id}`}>
                                                                    unverified
                                                                </Badge>
                                                            )}
                                                            {u.phone_verified && (
                                                                <span className="w-1.5 h-1.5 rounded-full bg-green-500" title="Verified" data-testid={`phone-verified-${u.id}`} />
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-zinc-300">—</span>
                                                    )}
                                                </TableCell>
                                                <TableCell><Badge variant={u.status === 'active' ? 'outline' : 'destructive'} className="rounded-none text-xs">{u.status}</Badge></TableCell>
                                                <TableCell>
                                                    {needsApproval ? (
                                                        <Badge
                                                            className={`rounded-none text-xs capitalize ${approval === 'approved' ? 'bg-zinc-900 text-white' : approval === 'pending' ? 'bg-amber-500 text-white' : 'bg-red-500 text-white'}`}
                                                            data-testid={`approval-badge-${u.id}`}
                                                        >
                                                            {approval}
                                                        </Badge>
                                                    ) : (
                                                        <span className="text-xs text-zinc-300">—</span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex gap-1 flex-wrap">
                                                        {needsApproval && approval === 'pending' && (
                                                            <>
                                                                <Button size="sm" className="rounded-none text-xs h-7 bg-zinc-900 text-white" onClick={() => approveUser(u.id)} disabled={savingApproval} data-testid={`approve-user-${u.id}`}>
                                                                    <CheckCircle className="w-3 h-3 mr-1" /> Approve
                                                                </Button>
                                                                <Button size="sm" variant="outline" className="rounded-none text-xs h-7 border-red-500 text-red-600" onClick={() => openRejectDialog(u)} disabled={savingApproval} data-testid={`reject-user-${u.id}`}>
                                                                    <ThumbsDown className="w-3 h-3 mr-1" /> Reject
                                                                </Button>
                                                            </>
                                                        )}
                                                        {needsApproval && approval === 'rejected' && (
                                                            <Button size="sm" className="rounded-none text-xs h-7 bg-zinc-900 text-white" onClick={() => approveUser(u.id)} disabled={savingApproval} data-testid={`approve-rejected-${u.id}`}>
                                                                <CheckCircle className="w-3 h-3 mr-1" /> Approve
                                                            </Button>
                                                        )}
                                                        {needsApproval && approval === 'approved' && (
                                                            <Button size="sm" variant="outline" className="rounded-none text-xs h-7" onClick={() => revokeApproval(u.id)} disabled={savingApproval} data-testid={`revoke-approval-${u.id}`}>
                                                                Revoke
                                                            </Button>
                                                        )}
                                                        <Button size="sm" variant="ghost" className="rounded-none text-xs h-7" onClick={() => viewUserDocs(u.id, u.name || u.company_name)} data-testid={`view-docs-${u.id}`}>
                                                            <Eye className="w-3 h-3 mr-1" /> Docs
                                                        </Button>
                                                        {u.role !== 'admin' && (
                                                            <Button size="sm" variant={u.status === 'active' ? 'outline' : 'default'} className="rounded-none text-xs h-7" onClick={() => toggleUserStatus(u.id, u.status)} data-testid={`toggle-status-${u.id}`}>
                                                                {u.status === 'active' ? <Ban className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
                                                            </Button>
                                                        )}
                                                        {u.role !== 'admin' && (
                                                            <Button size="sm" variant="ghost" className="rounded-none text-xs h-7 text-red-500 hover:text-red-700" onClick={() => confirmDeleteUser(u.id, u.name || u.company_name)} data-testid={`delete-user-${u.id}`}>
                                                                <X className="w-3 h-3" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                        );
                    })()}
                </TabsContent>

                {/* Medical Partners Tab — with Fee Management */}
                <TabsContent value="partners">
                    <h3 className="font-heading text-lg font-bold mb-4">Medical Partner Management</h3>
                    {partners.length === 0 ? (
                        <div className="text-center py-12 border border-zinc-200"><Heart className="w-12 h-12 text-zinc-200 mx-auto mb-4" /><p className="text-zinc-500">No medical partners registered</p></div>
                    ) : (
                        <div className="space-y-4" data-testid="partners-list">
                            {partners.map(p => {
                                const payout = payouts.find(x => x.partner_id === p.id);
                                return (
                                    <div key={p.id} className="border border-zinc-200 p-5" data-testid={`partner-${p.id}`}>
                                        <div className="flex items-start justify-between mb-3">
                                            <div>
                                                <p className="font-semibold">{p.name}</p>
                                                <p className="text-xs text-zinc-500">{p.email} | {p.clinic_name || 'No clinic name'}</p>
                                            </div>
                                            <Badge variant={p.status === 'active' ? 'outline' : 'destructive'} className="rounded-none text-xs">{p.status}</Badge>
                                        </div>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                                            <div className="bg-zinc-50 p-2"><p className="text-[10px] text-zinc-400 uppercase">Fee</p><p className="text-sm font-bold">${p.appointment_fee || '—'}</p></div>
                                            <div className="bg-zinc-50 p-2"><p className="text-[10px] text-zinc-400 uppercase">Commission</p><p className="text-sm font-bold">{p.commission_percent || '—'}%</p></div>
                                            <div className="bg-zinc-50 p-2"><p className="text-[10px] text-zinc-400 uppercase">Reports</p><p className="text-sm font-bold">{p.total_reports}</p></div>
                                            <div className="bg-zinc-50 p-2"><p className="text-[10px] text-zinc-400 uppercase">Owed</p><p className="text-sm font-bold">${payout?.pending?.toFixed(2) || '0.00'}</p></div>
                                        </div>
                                        {(!p.appointment_fee || !p.commission_percent) && (
                                            <div className="bg-amber-50 border border-amber-200 p-2 mb-3 text-xs text-amber-700" data-testid={`fee-warning-${p.id}`}>
                                                Fee/commission not configured. Click "Set Fees" to configure.
                                            </div>
                                        )}
                                        <div className="flex gap-2">
                                            <Button size="sm" variant="outline" className="rounded-none text-xs h-8" onClick={() => openFeeEditor(p)} data-testid={`set-fees-${p.id}`}>
                                                <Settings className="w-3 h-3 mr-1" /> Set Fees
                                            </Button>
                                            <Button size="sm" variant="ghost" className="rounded-none text-xs h-8" onClick={() => viewUserDocs(p.id, p.name)} data-testid={`partner-docs-${p.id}`}>
                                                <Eye className="w-3 h-3 mr-1" /> View Docs
                                            </Button>
                                            <Button size="sm" variant={p.status === 'active' ? 'outline' : 'default'} className="rounded-none text-xs h-8" onClick={() => toggleUserStatus(p.id, p.status)}>
                                                {p.status === 'active' ? <><Ban className="w-3 h-3 mr-1" /> Suspend</> : <><CheckCircle className="w-3 h-3 mr-1" /> Activate</>}
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </TabsContent>

                {/* Interests Manager Tab */}
                <TabsContent value="interests">
                    <div className="space-y-4" data-testid="interests-manager">
                        <div className="flex items-center justify-between">
                            <h3 className="font-heading text-lg font-bold">Interests Manager</h3>
                            <div className="flex gap-2">
                                <Input value={newCatName} onChange={e => setNewCatName(e.target.value)} placeholder="New category name" className="h-9 rounded-none w-48 text-sm" data-testid="new-category-input" />
                                <Button size="sm" onClick={addCategory} disabled={!newCatName.trim()} className="rounded-none h-9 bg-zinc-900 text-white" data-testid="add-category-btn">Add Category</Button>
                            </div>
                        </div>
                        {interestCategories.map(cat => (
                            <div key={cat.id} className={`border p-4 ${cat.active ? 'border-zinc-200' : 'border-red-200 bg-red-50 opacity-60'}`} data-testid={`cat-${cat.id}`}>
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="font-bold text-sm">{cat.name} <span className="text-zinc-400 font-normal">({cat.roles?.length || 0} roles)</span></h4>
                                    <div className="flex gap-1">
                                        <Button size="sm" variant={cat.active ? 'outline' : 'default'} className="rounded-none text-xs h-7" onClick={() => toggleCategoryActive(cat.id, cat.active)} data-testid={`toggle-cat-${cat.id}`}>{cat.active ? 'Deactivate' : 'Activate'}</Button>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-1.5 mb-2">
                                    {(cat.roles || []).map(r => (
                                        <span key={r.id} className={`inline-flex items-center gap-1 px-2 py-1 text-xs border ${r.active ? 'border-zinc-200' : 'border-red-200 line-through opacity-50'}`} data-testid={`role-${r.id}`}>
                                            {r.name}
                                            <button onClick={() => toggleRoleActive(r.id, r.active)} className="text-zinc-400 hover:text-red-600"><X className="w-2.5 h-2.5" /></button>
                                        </span>
                                    ))}
                                </div>
                                {addingRoleTo === cat.id ? (
                                    <div className="flex gap-2 mt-2">
                                        <Input value={newRoleName} onChange={e => setNewRoleName(e.target.value)} placeholder="Role name" className="h-8 rounded-none text-xs flex-1" data-testid={`new-role-input-${cat.id}`} onKeyDown={e => e.key === 'Enter' && addRole(cat.id)} />
                                        <Button size="sm" onClick={() => addRole(cat.id)} className="rounded-none h-8 text-xs bg-zinc-900 text-white" data-testid={`add-role-btn-${cat.id}`}>Add</Button>
                                        <Button size="sm" variant="ghost" onClick={() => { setAddingRoleTo(null); setNewRoleName(''); }} className="rounded-none h-8 text-xs">Cancel</Button>
                                    </div>
                                ) : (
                                    <Button size="sm" variant="ghost" className="rounded-none text-xs h-7 mt-1" onClick={() => setAddingRoleTo(cat.id)} data-testid={`show-add-role-${cat.id}`}>+ Add Role</Button>
                                )}
                            </div>
                        ))}
                    </div>
                </TabsContent>

                {/* Transactions */}
                <TabsContent value="transactions">
                    {(() => {
                        const q = txnSearch.trim().toLowerCase();
                        let filtered = transactions.filter(t => {
                            if (!q) return true;
                            const worker = users.find(u => u.id === t.user_id);
                            const fields = [
                                worker?.name, worker?.company_name, worker?.email,
                                t.stripe_payment_intent_id, String(t.amount || ''),
                                t.created_at ? new Date(t.created_at).toLocaleDateString() : '',
                                t.created_at ? new Date(t.created_at).toLocaleString() : ''
                            ].filter(Boolean).map(s => String(s).toLowerCase());
                            return fields.some(f => f.includes(q));
                        });
                        if (txnSort === 'date-desc') filtered = [...filtered].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
                        else if (txnSort === 'date-asc') filtered = [...filtered].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
                        else if (txnSort === 'amount-desc') filtered = [...filtered].sort((a, b) => Number(b.amount || 0) - Number(a.amount || 0));
                        else if (txnSort === 'amount-asc') filtered = [...filtered].sort((a, b) => Number(a.amount || 0) - Number(b.amount || 0));

                        return (
                            <>
                                <div className="flex flex-col sm:flex-row gap-3 mb-4" data-testid="txn-controls">
                                    <div className="relative flex-1">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                                        <Input placeholder="Search by worker, PI ID, amount, date..." value={txnSearch} onChange={e => setTxnSearch(e.target.value)} className="pl-10 h-11 rounded-none" data-testid="txn-search-input" />
                                    </div>
                                    <Select value={txnSort} onValueChange={setTxnSort}>
                                        <SelectTrigger className="w-full sm:w-56 h-11 rounded-none" data-testid="txn-sort-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="date-desc">Date: Newest first</SelectItem>
                                            <SelectItem value="date-asc">Date: Oldest first</SelectItem>
                                            <SelectItem value="amount-desc">Amount: Highest first</SelectItem>
                                            <SelectItem value="amount-asc">Amount: Lowest first</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                {filtered.length === 0 ? (
                                    <div className="text-center py-12 border border-zinc-200" data-testid="txn-empty">
                                        <DollarSign className="w-10 h-10 text-zinc-200 mx-auto mb-3" />
                                        <p className="text-sm text-zinc-500">{transactions.length === 0 ? 'No payment transactions recorded yet.' : 'No transactions match your search.'}</p>
                                    </div>
                                ) : (
                                    <div className="border border-zinc-200 overflow-x-auto" data-testid="transactions-table">
                                        <Table>
                                            <TableHeader><TableRow>
                                                <TableHead className="font-bold">Type</TableHead>
                                                <TableHead className="font-bold">Worker</TableHead>
                                                <TableHead className="font-bold">Amount</TableHead>
                                                <TableHead className="font-bold">Stripe PI</TableHead>
                                                <TableHead className="font-bold">Status</TableHead>
                                                <TableHead className="font-bold">Date</TableHead>
                                            </TableRow></TableHeader>
                                            <TableBody>
                                                {filtered.map(t => {
                                                    const worker = users.find(u => u.id === t.user_id);
                                                    return (
                                                        <TableRow key={t.id} data-testid={`txn-row-${t.id}`}>
                                                            <TableCell className="text-sm capitalize">{(t.type || '').replace('_', ' ')}</TableCell>
                                                            <TableCell className="text-sm">{worker?.name || worker?.company_name || (t.user_id ? t.user_id.slice(0,8) : '—')}</TableCell>
                                                            <TableCell className="text-sm font-medium">${Number(t.amount || 0).toFixed(2)} {t.currency}</TableCell>
                                                            <TableCell className="text-xs font-mono text-zinc-600">{t.stripe_payment_intent_id || '—'}</TableCell>
                                                            <TableCell><Badge variant="outline" className="rounded-none text-xs capitalize">{t.status}</Badge></TableCell>
                                                            <TableCell className="text-sm text-zinc-500">{t.created_at ? new Date(t.created_at).toLocaleString() : '—'}</TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </div>
                                )}
                            </>
                        );
                    })()}
                </TabsContent>

                {/* Submissions */}
                <TabsContent value="submissions">
                    <AdminSubmissions api={api} />
                </TabsContent>

                {/* Settings */}
                <TabsContent value="settings">
                    <PlatformSettings api={api} />
                    <LegalContentEditor api={api} />
                    <AdminCredentialsCard api={api} />
                </TabsContent>

                {/* Analytics */}
                <TabsContent value="analytics">
                    {analytics && (
                        <div className="space-y-6" data-testid="analytics-detail">
                            <div className="border border-zinc-200 p-6">
                                <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2"><TrendingUp className="w-5 h-5" /> Revenue</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                                    <div><p className="text-xs text-zinc-400 uppercase mb-1">Document Access Fees</p><p className="font-heading text-2xl font-black">${analytics.document_access_revenue}</p></div>
                                    <div><p className="text-xs text-zinc-400 uppercase mb-1">Medical Commissions</p><p className="font-heading text-2xl font-black">${analytics.medical_commission_revenue.toFixed(2)}</p></div>
                                    <div><p className="text-xs text-zinc-400 uppercase mb-1">Total Revenue</p><p className="font-heading text-2xl font-black">${analytics.total_revenue.toFixed(2)}</p></div>
                                </div>
                            </div>
                            <div className="border border-zinc-200 p-6">
                                <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5" /> Metrics</h3>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    <MetricItem label="Total Workers" value={analytics.total_workers} />
                                    <MetricItem label="Verified Workers" value={analytics.verified_workers} />
                                    <MetricItem label="Employers" value={analytics.total_employers} />
                                    <MetricItem label="Medical Partners" value={analytics.total_medical_partners} />
                                    <MetricItem label="Total Offers" value={analytics.total_offers} />
                                    <MetricItem label="Accepted Offers" value={analytics.accepted_offers} />
                                </div>
                            </div>
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            {/* Document Viewer Dialog */}
            <Dialog open={showDocs} onOpenChange={(o) => { if (!o && previewDoc) return; setShowDocs(o); }}>
                <DialogContent
                    className="max-w-lg rounded-none border-2 border-zinc-900 max-h-[80vh] overflow-y-auto"
                >
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">Documents — {selectedUserName}</DialogTitle>
                        <DialogDescription>Click &quot;View&quot; to preview each document inline</DialogDescription>
                    </DialogHeader>
                    {/* User contact info (worker only — admin-only PII view) */}
                    {selectedUserObj?.role === 'worker' && (
                        <div className="border border-zinc-200 p-3 mt-2" data-testid="admin-worker-contact">
                            <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2">Contact (admin-only)</p>
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="text-[10px] text-zinc-400 mb-0.5">Phone</p>
                                    {selectedUserObj.phone ? (
                                        <div className="flex items-center gap-2">
                                            <p className="text-sm font-mono" data-testid="admin-worker-phone">{selectedUserObj.phone}</p>
                                            {selectedUserObj.phone_verified ? (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-green-700" data-testid="admin-worker-phone-status-verified">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Verified
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-zinc-500" data-testid="admin-worker-phone-status-unverified">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-zinc-400" /> Not verified
                                                </span>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-zinc-400" data-testid="admin-worker-phone-empty">No phone number added</p>
                                    )}
                                </div>
                                <div className="text-right min-w-0">
                                    <p className="text-[10px] text-zinc-400 mb-0.5">Email</p>
                                    <p className="text-sm text-zinc-700 truncate" title={selectedUserObj.email}>{selectedUserObj.email}</p>
                                </div>
                            </div>
                        </div>
                    )}
                    {selectedUserObj?.video_intro_url && (
                        <div className="mt-4 border-t border-zinc-100 pt-4 space-y-2" data-testid="admin-video-intro">
                            <VideoIntroPlayer
                                url={selectedUserObj.video_intro_url}
                                platform={selectedUserObj.video_intro_platform}
                                testIdPrefix="admin-video-intro"
                            />
                            <div className="flex items-center gap-2 text-[11px] text-zinc-500">
                                <span className="uppercase tracking-wider font-bold">URL:</span>
                                <input
                                    readOnly
                                    value={selectedUserObj.video_intro_url}
                                    onClick={e => e.target.select()}
                                    className="flex-1 bg-zinc-50 border border-zinc-200 px-2 py-1 text-[11px] font-mono rounded-none"
                                    data-testid="admin-video-intro-url"
                                />
                            </div>
                        </div>
                    )}
                    <div className="space-y-2 mt-4">
                        {selectedUserDocs.length === 0 ? (
                            <p className="text-sm text-zinc-500 py-4 text-center">No documents uploaded</p>
                        ) : (
                            selectedUserDocs.map(doc => (
                                <div key={doc.id} className="flex items-center justify-between p-3 border border-zinc-200" data-testid={`admin-doc-${doc.id}`}>
                                    <div className="flex items-center gap-2 min-w-0">
                                        {doc.doc_type === 'video_intro' ? <Video className="w-4 h-4 text-zinc-500 flex-shrink-0" /> : <FileText className="w-4 h-4 text-zinc-500 flex-shrink-0" />}
                                        <div className="min-w-0">
                                            <p className="text-sm font-medium capitalize truncate">{getDocLabel(doc)}</p>
                                            <p className="text-[10px] text-zinc-400 truncate">{doc.original_filename}</p>
                                        </div>
                                    </div>
                                    <Button size="sm" variant="outline" className="rounded-none text-xs h-7 flex-shrink-0" onClick={() => openDocument(doc)} data-testid={`open-doc-${doc.id}`}>
                                        <Eye className="w-3 h-3 mr-1" /> View
                                    </Button>
                                </div>
                            ))
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Partner Fee Editor Dialog */}
            <Dialog open={showFeeEditor} onOpenChange={setShowFeeEditor}>
                <DialogContent className="max-w-sm rounded-none border-2 border-zinc-900">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">Set Partner Fees</DialogTitle>
                        <DialogDescription>{editingPartner?.name}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div>
                            <Label className="text-sm font-semibold">Appointment Fee (USD)</Label>
                            <Input type="number" value={feeAmount} onChange={e => setFeeAmount(e.target.value)} className="mt-1.5 h-12 rounded-none" placeholder="e.g., 120" data-testid="fee-amount-input" />
                            <p className="text-xs text-zinc-400 mt-1">Amount charged to worker when booking</p>
                        </div>
                        <div>
                            <Label className="text-sm font-semibold">Platform Commission (%)</Label>
                            <Input type="number" value={commissionPct} onChange={e => setCommissionPct(e.target.value)} className="mt-1.5 h-12 rounded-none" placeholder="e.g., 40" min="0" max="100" data-testid="commission-input" />
                            <p className="text-xs text-zinc-400 mt-1">Platform takes this %, partner gets the rest</p>
                        </div>
                        {feeAmount && commissionPct && (
                            <div className="bg-zinc-50 p-3 border border-zinc-200" data-testid="fee-preview">
                                <p className="text-xs text-zinc-400 uppercase mb-2">Preview</p>
                                <div className="flex justify-between text-sm"><span>Worker pays:</span><span className="font-bold">${parseFloat(feeAmount).toFixed(2)}</span></div>
                                <div className="flex justify-between text-sm"><span>Platform keeps ({commissionPct}%):</span><span className="font-bold">${(parseFloat(feeAmount) * parseFloat(commissionPct) / 100).toFixed(2)}</span></div>
                                <div className="flex justify-between text-sm"><span>Partner earns:</span><span className="font-bold">${(parseFloat(feeAmount) * (1 - parseFloat(commissionPct) / 100)).toFixed(2)}</span></div>
                            </div>
                        )}
                    </div>
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setShowFeeEditor(false)} className="rounded-none">Cancel</Button>
                        <Button onClick={saveFees} disabled={savingFees || !feeAmount || !commissionPct} className="rounded-none bg-zinc-900 text-white font-semibold" data-testid="save-fees-btn">
                            {savingFees ? 'Saving...' : 'Save Fees'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete User Confirmation Dialog */}
            <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <DialogContent className="max-w-sm rounded-none border-2 border-red-900">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl text-red-700">Delete User</DialogTitle>
                        <DialogDescription>Are you sure you want to permanently delete <strong>{deletingUserName}</strong> and all associated data? This cannot be undone.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} className="rounded-none" data-testid="cancel-delete-btn">Cancel</Button>
                        <Button onClick={executeDeleteUser} disabled={deleting} className="rounded-none bg-red-600 text-white hover:bg-red-700" data-testid="confirm-delete-btn">{deleting ? 'Deleting...' : 'Delete Permanently'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reject Approval Dialog */}
            <Dialog open={showRejectDialog} onOpenChange={(o) => { if (!o) { setShowRejectDialog(false); setRejectingUser(null); setRejectReason(''); } }}>
                <DialogContent className="max-w-md rounded-none border-2 border-zinc-900">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">Reject Account</DialogTitle>
                        <DialogDescription>
                            Enter a reason for rejecting <strong>{rejectingUser?.company_name || rejectingUser?.name}</strong>. The user will be notified with this reason.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="mt-2">
                        <Label htmlFor="reject-reason-input" className="text-xs uppercase tracking-wider text-zinc-500">Rejection reason (required)</Label>
                        <Textarea
                            id="reject-reason-input"
                            value={rejectReason}
                            onChange={e => setRejectReason(e.target.value)}
                            placeholder="e.g., Unable to verify company registration. Please resubmit with valid documents."
                            className="mt-1.5 rounded-none min-h-[100px]"
                            data-testid="reject-reason-input"
                        />
                    </div>
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setShowRejectDialog(false)} className="rounded-none" data-testid="cancel-reject-btn">Cancel</Button>
                        <Button onClick={submitReject} disabled={!rejectReason.trim() || savingApproval} className="rounded-none bg-red-600 text-white hover:bg-red-700" data-testid="confirm-reject-btn">
                            {savingApproval ? 'Rejecting...' : 'Reject Account'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Universal Document Preview Modal (Fix 5) */}
            <DocumentPreviewModal
                open={!!previewDoc}
                onClose={() => setPreviewDoc(null)}
                doc={previewDoc}
            />
        </div>
    );
}

function StatCard({ icon, label, value, sub, testid }) {
    return (
        <div className="border border-zinc-200 p-3 sm:p-4" data-testid={testid}>
            <div className="flex items-center gap-2 mb-2 text-zinc-400">{icon}<span className="text-[10px] sm:text-xs tracking-[0.1em] uppercase font-bold">{label}</span></div>
            <p className="font-heading text-xl sm:text-2xl font-black truncate">{value}</p>
            {sub && <p className="text-[10px] sm:text-xs text-zinc-400 mt-1">{sub}</p>}
        </div>
    );
}

function MetricItem({ label, value }) {
    return (<div className="p-3 bg-zinc-50"><p className="text-xs text-zinc-400 mb-1">{label}</p><p className="font-heading text-lg font-bold">{value}</p></div>);
}

function PlatformSettings({ api }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [feeInput, setFeeInput] = useState('');
    const [currentFee, setCurrentFee] = useState(null);
    const [isDefault, setIsDefault] = useState(false);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const fetchFee = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/admin/settings/document-access-fee');
            setCurrentFee(Number(data.fee));
            setFeeInput(Number(data.fee).toFixed(2));
            setIsDefault(!!data.is_default);
            setUpdatedAt(data.updated_at);
        } catch (err) {
            setError('Could not load fee settings.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchFee(); /* eslint-disable-line */ }, []);

    const validate = (val) => {
        if (val === '' || val === null || val === undefined) return 'Please enter a valid amount';
        const n = Number(val);
        if (Number.isNaN(n)) return 'Please enter a valid amount';
        if (n < 1) return 'Fee must be at least $1.00';
        if (n > 10000) return 'Fee must be at most $10,000.00';
        return null;
    };

    const save = async () => {
        setError(''); setSuccess('');
        const v = validate(feeInput);
        if (v) { setError(v); return; }
        setSaving(true);
        try {
            const { data } = await api.put('/admin/settings/document-access-fee', { fee: Number(feeInput) });
            setCurrentFee(Number(data.fee));
            setFeeInput(Number(data.fee).toFixed(2));
            setIsDefault(false);
            setUpdatedAt(data.updated_at);
            setSuccess('Fee updated successfully.');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.detail || 'Could not update fee.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="platform-settings">
            <div className="border border-zinc-200 p-5">
                <h3 className="font-heading text-lg font-bold mb-1">Document Access Fee (USD)</h3>
                <p className="text-xs text-zinc-500 mb-4">This is the fee workers pay to unlock their visa and flight ticket documents after visa approval. Minimum: $1.00. Maximum: $10,000.00. Changes take effect immediately for all new payments.</p>

                {loading ? (
                    <p className="text-sm text-zinc-400">Loading...</p>
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                            <div className="sm:col-span-2">
                                <Label htmlFor="fee-input" className="text-xs font-semibold">Fee Amount (USD)</Label>
                                <div className="relative mt-1.5">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 font-bold">$</span>
                                    <Input
                                        id="fee-input"
                                        type="number"
                                        step="0.01"
                                        min="1"
                                        max="10000"
                                        value={feeInput}
                                        onChange={(e) => { setFeeInput(e.target.value); setError(''); setSuccess(''); }}
                                        className="h-12 rounded-none pl-7 font-mono"
                                        data-testid="fee-input"
                                    />
                                </div>
                            </div>
                            <Button
                                onClick={save}
                                disabled={saving || loading}
                                className="h-12 rounded-none bg-zinc-900 text-white font-semibold"
                                data-testid="save-fee-btn"
                            >
                                {saving ? 'Saving...' : 'Update Fee'}
                            </Button>
                        </div>

                        {error && <p className="text-sm text-red-600 mt-3" data-testid="fee-error">{error}</p>}
                        {success && (
                            <p className="text-sm text-green-700 mt-3 flex items-center gap-1" data-testid="fee-success">
                                <CheckCircle className="w-4 h-4" /> {success}
                            </p>
                        )}

                        <div className="mt-5 pt-4 border-t border-zinc-100 grid grid-cols-2 gap-3 text-xs">
                            <div>
                                <p className="text-zinc-400 uppercase tracking-wide">Current Fee</p>
                                <p className="font-heading text-base font-bold mt-0.5" data-testid="current-fee-display">
                                    ${currentFee !== null ? Number(currentFee).toFixed(2) : '—'} USD
                                </p>
                                {isDefault && <p className="text-[10px] text-zinc-400">(default — not yet customised)</p>}
                            </div>
                            <div>
                                <p className="text-zinc-400 uppercase tracking-wide">Last Updated</p>
                                <p className="text-zinc-700 mt-0.5">
                                    {updatedAt ? new Date(updatedAt).toLocaleString() : '—'}
                                </p>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

function LegalContentEditor({ api }) {
    const TYPES = [
        { key: 'privacy', label: 'Privacy Policy', publicPath: '/privacy' },
    ];
    const [activeType, setActiveType] = useState('privacy');
    const [docs, setDocs] = useState({ privacy: null });
    const [drafts, setDrafts] = useState({ privacy: '' });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [confirmReset, setConfirmReset] = useState(false);

    const loadDoc = useCallback(async (type) => {
        const { data } = await api.get(`/admin/legal/${type}`);
        return data;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            try {
                const privacy = await loadDoc('privacy');
                if (!alive) return;
                setDocs({ privacy });
                setDrafts({ privacy: privacy.content });
            } catch (e) {
                if (alive) setError('Could not load legal documents.');
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [loadDoc]);

    const current = docs[activeType];
    const draft = drafts[activeType];
    const dirty = current && draft !== current.content;

    const save = async () => {
        setError(''); setSuccess('');
        if (!draft || !draft.trim()) { setError('Content cannot be empty.'); return; }
        setSaving(true);
        try {
            const { data } = await api.put(`/admin/legal/${activeType}`, { content: draft });
            setDocs(prev => ({ ...prev, [activeType]: data }));
            setDrafts(prev => ({ ...prev, [activeType]: data.content }));
            setSuccess('Privacy Policy updated.');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.detail || 'Could not save.');
        } finally {
            setSaving(false);
        }
    };

    const resetToDefault = async () => {
        setError(''); setSuccess(''); setResetting(true);
        try {
            const { data } = await api.post(`/admin/legal/${activeType}/reset`);
            setDocs(prev => ({ ...prev, [activeType]: data }));
            setDrafts(prev => ({ ...prev, [activeType]: data.content }));
            setSuccess('Reset to default content.');
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.detail || 'Could not reset.');
        } finally {
            setResetting(false);
            setConfirmReset(false);
        }
    };

    const meta = TYPES.find(t => t.key === activeType);

    return (
        <div className="border border-zinc-200 p-5 mt-6" data-testid="legal-content-editor">
            <h3 className="font-heading text-lg font-bold mb-1">Legal Content</h3>
            <p className="text-xs text-zinc-500 mb-4">
                Edit the Privacy Policy displayed at <code className="font-mono text-[11px]">/privacy</code>. Supports markdown headings (<code className="font-mono text-[11px]">#</code>, <code className="font-mono text-[11px]">##</code>), bullet lists (<code className="font-mono text-[11px]">-</code>), <code className="font-mono text-[11px]">**bold**</code>, and <code className="font-mono text-[11px]">---</code> horizontal rules. Changes go live instantly — no redeploy required. <em>Terms of Service is now shown in a role-tabbed layout and is versioned in source; contact your developer to update it.</em>
            </p>

            <div className="flex gap-2 mb-4 border-b border-zinc-200">
                {TYPES.map(t => (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => { setActiveType(t.key); setError(''); setSuccess(''); }}
                        className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider border-b-2 transition-colors ${activeType === t.key ? 'border-zinc-900 text-zinc-900' : 'border-transparent text-zinc-400 hover:text-zinc-700'}`}
                        data-testid={`legal-tab-${t.key}`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <p className="text-sm text-zinc-400">Loading...</p>
            ) : (
                <>
                    <div className="flex items-center gap-3 mb-2 text-[11px] text-zinc-500">
                        <span className={`px-2 py-0.5 ${current?.is_default ? 'bg-zinc-100 text-zinc-600' : 'bg-zinc-900 text-white'} uppercase tracking-wider font-bold`} data-testid={`legal-status-${activeType}`}>
                            {current?.is_default ? 'Default' : 'Customised'}
                        </span>
                        <span data-testid={`legal-updated-${activeType}`}>
                            {current?.updated_at ? `Last updated ${new Date(current.updated_at).toLocaleString()}` : 'Never edited'}
                        </span>
                        <a href={meta.publicPath} target="_blank" rel="noreferrer" className="ml-auto text-zinc-700 hover:text-zinc-900 underline" data-testid={`legal-preview-${activeType}`}>
                            View public page →
                        </a>
                    </div>

                    <Textarea
                        value={draft}
                        onChange={(e) => { setDrafts(prev => ({ ...prev, [activeType]: e.target.value })); setError(''); setSuccess(''); }}
                        className="rounded-none font-mono text-xs leading-relaxed min-h-[420px]"
                        spellCheck={false}
                        data-testid={`legal-textarea-${activeType}`}
                    />
                    <p className="text-[10px] text-zinc-400 mt-1 text-right">{draft.length.toLocaleString()} / 50,000 characters</p>

                    {error && <p className="text-sm text-red-600 mt-3" data-testid="legal-error">{error}</p>}
                    {success && (
                        <p className="text-sm text-green-700 mt-3 flex items-center gap-1" data-testid="legal-success">
                            <CheckCircle className="w-4 h-4" /> {success}
                        </p>
                    )}

                    <div className="flex items-center gap-3 mt-4 flex-wrap">
                        <Button
                            onClick={save}
                            disabled={saving || !dirty}
                            className="h-11 rounded-none bg-zinc-900 text-white font-semibold px-6"
                            data-testid={`legal-save-${activeType}`}
                        >
                            {saving ? 'Saving...' : (dirty ? 'Save Changes' : 'Saved')}
                        </Button>
                        <Button
                            onClick={() => setDrafts(prev => ({ ...prev, [activeType]: current.content }))}
                            disabled={!dirty || saving}
                            variant="outline"
                            className="h-11 rounded-none font-semibold"
                            data-testid={`legal-discard-${activeType}`}
                        >
                            Discard
                        </Button>
                        {!current?.is_default && (
                            <Button
                                onClick={() => setConfirmReset(true)}
                                disabled={saving || resetting}
                                variant="outline"
                                className="h-11 rounded-none font-semibold ml-auto border-red-200 text-red-700 hover:bg-red-50"
                                data-testid={`legal-reset-${activeType}`}
                            >
                                Reset to Default
                            </Button>
                        )}
                    </div>
                </>
            )}

            <Dialog open={confirmReset} onOpenChange={setConfirmReset}>
                <DialogContent className="rounded-none border-2 border-zinc-900" data-testid="legal-reset-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl font-black">Reset {meta?.label}?</DialogTitle>
                        <DialogDescription className="text-sm text-zinc-600">
                            This will replace the current custom content with the built-in default and immediately update the public page. This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setConfirmReset(false)} className="rounded-none" data-testid="legal-reset-cancel">Cancel</Button>
                        <Button onClick={resetToDefault} disabled={resetting} className="rounded-none bg-red-700 text-white hover:bg-red-800" data-testid="legal-reset-confirm">
                            {resetting ? 'Resetting...' : 'Reset to Default'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}


function AdminCredentialsCard({ api }) {
    const { user, refreshUser } = useAuth();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        if (!currentPassword) { setError('Current password is required.'); return; }
        if (!newEmail && !newPassword) { setError('Provide a new email or new password.'); return; }
        if (newPassword && newPassword.length < 8) { setError('New password must be at least 8 characters.'); return; }
        if (newPassword && newPassword !== confirmPassword) { setError('New password and confirmation do not match.'); return; }
        setSaving(true);
        try {
            const payload = { current_password: currentPassword };
            if (newEmail && newEmail !== user?.email) payload.new_email = newEmail.trim().toLowerCase();
            if (newPassword) payload.new_password = newPassword;
            const { data } = await api.put('/admin/settings/credentials', payload);
            if (data.token) localStorage.setItem('gulfbridge_token', data.token);
            await refreshUser();
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setNewEmail('');
            setSuccess('Login credentials updated successfully.');
            setTimeout(() => setSuccess(''), 4000);
        } catch (err) {
            setError(err.response?.data?.detail || 'Could not update credentials.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="border border-zinc-200 p-5 mt-6" data-testid="admin-credentials-card">
            <h3 className="font-heading text-lg font-bold mb-1">Login &amp; Security</h3>
            <p className="text-xs text-zinc-500 mb-4">
                Update your admin email or set a stronger password. You will stay logged in after the change.
            </p>
            <form onSubmit={submit} className="space-y-4 max-w-md">
                <div>
                    <Label className="text-xs uppercase tracking-wider text-zinc-500">Current admin email</Label>
                    <p className="text-sm font-mono mt-1" data-testid="admin-current-email">{user?.email || '—'}</p>
                </div>
                <div>
                    <Label htmlFor="cred-current-password" className="text-xs font-semibold">Current password (required)</Label>
                    <div className="relative mt-1.5">
                        <Input
                            id="cred-current-password"
                            type={showCurrent ? 'text' : 'password'}
                            value={currentPassword}
                            onChange={e => { setCurrentPassword(e.target.value); setError(''); setSuccess(''); }}
                            className="h-12 rounded-none pr-10"
                            data-testid="cred-current-password-input"
                            autoComplete="current-password"
                        />
                        <button type="button" onClick={() => setShowCurrent(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-500 uppercase tracking-wider" data-testid="toggle-current-password">{showCurrent ? 'Hide' : 'Show'}</button>
                    </div>
                </div>
                <div>
                    <Label htmlFor="cred-new-email" className="text-xs font-semibold">New email <span className="font-normal text-zinc-400">(optional)</span></Label>
                    <Input
                        id="cred-new-email"
                        type="email"
                        value={newEmail}
                        onChange={e => { setNewEmail(e.target.value); setError(''); setSuccess(''); }}
                        className="mt-1.5 h-12 rounded-none"
                        placeholder={user?.email || 'admin@yourdomain.com'}
                        data-testid="cred-new-email-input"
                        autoComplete="off"
                    />
                </div>
                <div>
                    <Label htmlFor="cred-new-password" className="text-xs font-semibold">New password <span className="font-normal text-zinc-400">(optional, min 8 chars)</span></Label>
                    <div className="relative mt-1.5">
                        <Input
                            id="cred-new-password"
                            type={showNew ? 'text' : 'password'}
                            value={newPassword}
                            onChange={e => { setNewPassword(e.target.value); setError(''); setSuccess(''); }}
                            className="h-12 rounded-none pr-10"
                            data-testid="cred-new-password-input"
                            autoComplete="new-password"
                        />
                        <button type="button" onClick={() => setShowNew(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-500 uppercase tracking-wider" data-testid="toggle-new-password">{showNew ? 'Hide' : 'Show'}</button>
                    </div>
                </div>
                {newPassword && (
                    <div>
                        <Label htmlFor="cred-confirm-password" className="text-xs font-semibold">Confirm new password</Label>
                        <Input
                            id="cred-confirm-password"
                            type={showNew ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={e => { setConfirmPassword(e.target.value); setError(''); setSuccess(''); }}
                            className="mt-1.5 h-12 rounded-none"
                            data-testid="cred-confirm-password-input"
                            autoComplete="new-password"
                        />
                    </div>
                )}

                {error && <p className="text-sm text-red-600" data-testid="cred-error">{error}</p>}
                {success && (
                    <p className="text-sm text-green-700 flex items-center gap-1" data-testid="cred-success">
                        <CheckCircle className="w-4 h-4" /> {success}
                    </p>
                )}

                <Button
                    type="submit"
                    disabled={saving}
                    className="h-12 rounded-none bg-zinc-900 text-white font-semibold"
                    data-testid="save-credentials-btn"
                >
                    {saving ? 'Saving...' : 'Update Credentials'}
                </Button>
            </form>
        </div>
    );
}

function AdminSubmissions({ api }) {
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [detailSub, setDetailSub] = useState(null);
    const [confirmSub, setConfirmSub] = useState(null);
    const [marking, setMarking] = useState(false);
    const [error, setError] = useState('');
    const [previewAttachment, setPreviewAttachment] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/admin/submissions');
            setSubmissions(data.submissions || []);
        } catch (e) {
            setError('Could not load submissions.');
        } finally {
            setLoading(false);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const onRefresh = (e) => {
            const keys = e.detail?.keys || [];
            if (keys.includes('submissions')) load();
        };
        window.addEventListener(REFRESH_EVENT, onRefresh);
        return () => window.removeEventListener(REFRESH_EVENT, onRefresh);
    }, [load]);

    const attachmentUrl = (submissionId, attachmentId) =>
        `${api.defaults.baseURL}/submissions/${submissionId}/attachments/${attachmentId}`;

    const filtered = submissions.filter((s) => {
        if (statusFilter !== 'all' && s.status !== statusFilter) return false;
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            const hay = `${s.employer_company_name || ''} ${s.title || ''}`.toLowerCase();
            if (!hay.includes(q)) return false;
        }
        return true;
    });

    const formatDT = (iso) => {
        if (!iso) return '—';
        try {
            const d = new Date(iso);
            return d.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', ' at');
        } catch { return iso; }
    };

    const StatusPill = ({ status }) => {
        const isRepaid = status === 'repaid';
        return (
            <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${isRepaid ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-amber-100 text-amber-800 border border-amber-200'}`}>
                {isRepaid ? 'Repaid' : 'Pending'}
            </span>
        );
    };

    const doMarkRepaid = async () => {
        if (!confirmSub) return;
        setMarking(true);
        try {
            const { data } = await api.patch(`/admin/submissions/${confirmSub.id}/repaid`);
            setSubmissions((prev) => prev.map((s) => (s.id === data.submission.id ? data.submission : s)));
            if (detailSub && detailSub.id === data.submission.id) setDetailSub(data.submission);
            setConfirmSub(null);
        } catch (e) {
            setError(e.response?.data?.detail || 'Could not mark as repaid.');
        } finally {
            setMarking(false);
        }
    };

    const pendingCount = submissions.filter((s) => s.status === 'pending').length;

    return (
        <div data-testid="admin-submissions">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                <div>
                    <h2 className="font-heading text-lg font-bold">Submissions</h2>
                    <p className="text-xs text-zinc-500">{submissions.length} total · {pendingCount} pending</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search company or title..."
                            className="pl-8 h-9 w-64 rounded-none text-sm"
                            data-testid="admin-submissions-search"
                        />
                    </div>
                    <div className="flex items-center gap-1" data-testid="admin-submissions-filter">
                        {['all', 'pending', 'repaid'].map((s) => (
                            <Button
                                key={s}
                                size="sm"
                                variant={statusFilter === s ? 'default' : 'outline'}
                                className="rounded-none text-xs h-9 capitalize"
                                onClick={() => setStatusFilter(s)}
                                data-testid={`admin-submissions-filter-${s}`}
                            >
                                {s}
                            </Button>
                        ))}
                    </div>
                </div>
            </div>

            {error && (
                <div className="border border-red-200 bg-red-50 p-3 mb-4 text-sm text-red-700" data-testid="admin-submissions-error">{error}</div>
            )}

            {loading ? (
                <p className="text-sm text-zinc-400">Loading...</p>
            ) : filtered.length === 0 ? (
                <div className="border border-dashed border-zinc-200 p-8 text-center text-sm text-zinc-500" data-testid="admin-submissions-empty">
                    No submissions match the current filters.
                </div>
            ) : (
                <div className="border border-zinc-200 overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Employer</TableHead>
                                <TableHead>Title</TableHead>
                                <TableHead>Submitted</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filtered.map((s) => (
                                <TableRow key={s.id} data-testid={`admin-submission-row-${s.id}`}>
                                    <TableCell className="font-semibold">{s.employer_company_name || '—'}</TableCell>
                                    <TableCell className="max-w-md truncate">{s.title}</TableCell>
                                    <TableCell className="text-xs text-zinc-500">{formatDT(s.submitted_at)}</TableCell>
                                    <TableCell><StatusPill status={s.status} /></TableCell>
                                    <TableCell className="text-right">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="rounded-none h-8 text-xs"
                                            onClick={() => setDetailSub(s)}
                                            data-testid={`admin-submission-view-${s.id}`}
                                        >
                                            <Eye className="w-3.5 h-3.5 mr-1" /> View
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* Detail modal */}
            <Dialog open={!!detailSub} onOpenChange={(v) => { if (!v) setDetailSub(null); }}>
                <DialogContent
                    className="rounded-none max-w-2xl border-2 border-zinc-900 max-h-[92vh] overflow-y-auto"
                    data-testid="admin-submission-detail"
                >
                    {detailSub && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-heading text-xl font-black break-words">{detailSub.title}</DialogTitle>
                                <DialogDescription className="text-xs text-zinc-500">
                                    From <strong className="text-zinc-800">{detailSub.employer_company_name}</strong> · {formatDT(detailSub.submitted_at)}
                                </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4">
                                <div><StatusPill status={detailSub.status} /></div>
                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Message</p>
                                    <p className="text-sm text-zinc-800 whitespace-pre-wrap">{detailSub.message}</p>
                                </div>
                                {detailSub.attachments?.length > 0 && (
                                    <div>
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Attachments</p>
                                        <ul className="space-y-1" data-testid="admin-submission-attachments">
                                            {detailSub.attachments.map((a) => (
                                                <li key={a.id} className="flex items-center gap-2 text-sm border border-zinc-200 px-2 py-1.5">
                                                    <FileText className="w-3.5 h-3.5 text-zinc-400" />
                                                    <span className="truncate flex-1">{a.filename}</span>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="rounded-none h-7 text-[11px]"
                                                        onClick={() => setPreviewAttachment({ ...a, submissionId: detailSub.id })}
                                                        data-testid={`admin-submission-attachment-view-${a.id}`}
                                                    >
                                                        View
                                                    </Button>
                                                    <a
                                                        href={attachmentUrl(detailSub.id, a.id)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-xs underline text-zinc-700 hover:text-zinc-900"
                                                        download={a.filename}
                                                        data-testid={`admin-submission-attachment-download-${a.id}`}
                                                    >
                                                        Download
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                                    <div>
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Email Contact</p>
                                        <a href={`mailto:${detailSub.email_contact}`} className="underline break-all">{detailSub.email_contact}</a>
                                    </div>
                                    {detailSub.whatsapp_contact && (
                                        <div>
                                            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">WhatsApp</p>
                                            <a
                                                href={`https://wa.me/${detailSub.whatsapp_contact.replace(/[^\d]/g, '')}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="underline break-all"
                                            >
                                                {detailSub.whatsapp_contact}
                                            </a>
                                        </div>
                                    )}
                                </div>
                                {detailSub.status === 'repaid' && (
                                    <div className="border border-green-200 bg-green-50 p-2 text-xs text-green-800">
                                        <div className="font-semibold">Marked as Repaid</div>
                                        <div>{formatDT(detailSub.repaid_at)}{detailSub.repaid_by ? ` · admin id ${detailSub.repaid_by.slice(0, 8)}…` : ''}</div>
                                    </div>
                                )}
                            </div>
                            <DialogFooter className="gap-2 pt-2">
                                <Button variant="outline" onClick={() => setDetailSub(null)} className="rounded-none" data-testid="admin-submission-close-btn">Close</Button>
                                {detailSub.status === 'pending' && (
                                    <Button
                                        onClick={() => setConfirmSub(detailSub)}
                                        className="rounded-none bg-green-700 text-white hover:bg-green-800"
                                        data-testid="admin-submission-mark-repaid-btn"
                                    >
                                        <CheckCircle className="w-4 h-4 mr-1" /> Mark as Repaid
                                    </Button>
                                )}
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Confirmation dialog */}
            <Dialog open={!!confirmSub} onOpenChange={(v) => { if (!v) setConfirmSub(null); }}>
                <DialogContent className="rounded-none border-2 border-zinc-900" data-testid="admin-submission-confirm-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl font-black">Mark this submission as Repaid?</DialogTitle>
                        <DialogDescription className="text-sm text-zinc-600">
                            The employer will be notified.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setConfirmSub(null)} className="rounded-none" data-testid="admin-submission-confirm-cancel">Cancel</Button>
                        <Button
                            onClick={doMarkRepaid}
                            disabled={marking}
                            className="rounded-none bg-green-700 text-white hover:bg-green-800"
                            data-testid="admin-submission-confirm-yes"
                        >
                            {marking ? 'Marking...' : 'Mark as Repaid'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Attachment preview */}
            {previewAttachment && (
                <Dialog open onOpenChange={(v) => { if (!v) setPreviewAttachment(null); }}>
                    <DialogContent className="rounded-none max-w-4xl border-2 border-zinc-900 max-h-[92vh] overflow-hidden">
                        <DialogHeader>
                            <DialogTitle className="font-heading text-lg font-bold break-words">{previewAttachment.filename}</DialogTitle>
                        </DialogHeader>
                        <div className="h-[70vh]">
                            {previewAttachment.content_type?.startsWith('image/') ? (
                                <img
                                    src={attachmentUrl(previewAttachment.submissionId, previewAttachment.id)}
                                    alt={previewAttachment.filename}
                                    className="max-w-full max-h-full object-contain mx-auto"
                                />
                            ) : (
                                <iframe
                                    title={previewAttachment.filename}
                                    src={attachmentUrl(previewAttachment.submissionId, previewAttachment.id)}
                                    className="w-full h-full border-0"
                                />
                            )}
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setPreviewAttachment(null)} className="rounded-none">Close</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}


